/** Parses a Date, ISO string, or epoch ms into epoch ms; returns undefined for invalid/non-finite inputs. */
export function readCreatedAt(value: unknown): number | undefined {
  if (value instanceof Date) {
    const ts = value.getTime();
    return Number.isFinite(ts) ? ts : undefined;
  }
  if (typeof value === 'string') {
    const ts = Date.parse(value);
    return Number.isFinite(ts) ? ts : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return undefined;
}

export interface StepTiming {
  startedAt: number;
  completedAt?: number;
}

export type StepTimingsMap = Record<string, StepTiming>;

/** Persisted shape — `gapBeforeMs` is back-compat noise; `buildTrailTimeline` ignores it. */
export interface PersistedStepTiming extends StepTiming {
  completedAt: number;
  gapBeforeMs?: number;
}

export type PersistedStepTimings = Record<string, PersistedStepTiming>;

/** Drops malformed entries from `activityTrailStepTimings`; returns undefined if everything is rejected. */
export function parseStepTimings(value: unknown): PersistedStepTimings | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const out: PersistedStepTimings = {};
  let kept = 0;
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (!entry || typeof entry !== 'object') continue;
    const { startedAt, completedAt, gapBeforeMs } = entry as Record<string, unknown>;
    if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) continue;
    if (typeof completedAt !== 'number' || !Number.isFinite(completedAt)) continue;
    if (completedAt < startedAt) continue;
    const validGap =
      typeof gapBeforeMs === 'number' && Number.isFinite(gapBeforeMs) && gapBeforeMs >= 0
        ? gapBeforeMs
        : undefined;
    out[key] = { startedAt, completedAt, ...(validGap !== undefined && { gapBeforeMs: validGap }) };
    kept++;
  }
  return kept > 0 ? out : undefined;
}

export interface DeriveTimeAnchorsParams {
  isMessageRunning: boolean;
  trailMountedAt: number;
  /** Authoritative request start (epoch ms) — same value used to backdate persisted `created_at`. */
  requestStartedAt: number | undefined;
  createdAtMs: number | undefined;
  totalDurationMs: number | undefined;
  /** Captured at the running → finished transition; bridges until `stream_duration_ms` lands. */
  liveFinishedAt: number | undefined;
  stepTimings: StepTimingsMap;
  now: number;
}

export interface TrailTimeAnchors {
  messageStartAt: number;
  messageEndAt: number;
}

/** Resolves `messageStartAt`/`messageEndAt` from persisted, live, and real-time inputs, guarding against clock skew, parallel-batch gaps, and cloud-rehydration inflation. */
export function deriveTimeAnchors({
  isMessageRunning,
  trailMountedAt,
  requestStartedAt,
  createdAtMs,
  totalDurationMs,
  liveFinishedAt,
  stepTimings,
  now,
}: DeriveTimeAnchorsParams): TrailTimeAnchors {
  let latestCompletedAt: number | undefined;
  let latestStartedAt: number | undefined;
  for (const t of Object.values(stepTimings)) {
    if (
      t.completedAt !== undefined &&
      (latestCompletedAt === undefined || t.completedAt > latestCompletedAt)
    ) {
      latestCompletedAt = t.completedAt;
    }
    if (latestStartedAt === undefined || t.startedAt > latestStartedAt) {
      latestStartedAt = t.startedAt;
    }
  }

  // We don't derive `start` from the earliest observed timing — observation
  // lands AFTER mount, so picking it would shift the anchor forward and
  // make the live header counter visibly tick backward as steps arrive.
  const start = requestStartedAt ?? createdAtMs ?? trailMountedAt;

  // Persisted duration wins over a transient `isMessageRunning=true` from
  // cloud rehydration — without this clamp an old hydrated message could
  // produce messageEndAt = current wall clock and inflate row durations
  // to days.
  if (totalDurationMs !== undefined) {
    return { messageStartAt: start, messageEndAt: start + totalDurationMs };
  }

  if (isMessageRunning) {
    // Clamp to the latest observed timing — `latestStartedAt` matters for
    // parallel batches where multiple tools dispatch between ticks ahead of
    // `tickNow`, otherwise the cursor falls behind and emits phantom
    // thinking-gaps above each parallel row until the next tick. Skip the
    // ?? 0 trick: epoch-0 winning a degenerate max is never the answer,
    // filter undefined and feed only real numbers.
    const candidates = [start, now, latestCompletedAt, latestStartedAt].filter(
      (v): v is number => v !== undefined,
    );
    return { messageStartAt: start, messageEndAt: Math.max(...candidates) };
  }

  const end = liveFinishedAt ?? latestCompletedAt ?? start;
  return { messageStartAt: start, messageEndAt: Math.max(start, end) };
}
